import { Config } from '../../config';
import {
  IHttpClient,
  IAuthConsumer,
  RefreshAuthDelegate,
} from '../../services/HttpClient/IHttpClient';
import {
  IStorageProvider,
  StorageKeys,
} from '../../services/StorageProvider/IStorageProvider';
import { randomPassword } from '../../utils/miscellaneous';
import {
  SignInRequest,
  TokensResponse,
  SignUpRequest,
  VerifyCodeRequest,
  SignInResponse,
  GetTokensFromRefreshTokenResponse,
} from './authorization.models';
import { IAuthorizationApi } from './IAuthorizationApi';

export class AuthorizationApi
  implements IAuthorizationApi, RefreshAuthDelegate
{
  private waitingPromise: Promise<TokensResponse> | null | undefined;

  constructor(
    private readonly httpClient: IHttpClient,
    private readonly authConsumers: IAuthConsumer[],
    private readonly storageProvider: IStorageProvider,
    private readonly onAuthenticationFailed: () => void
  ) {
    authConsumers.forEach(consumer => consumer.setupRefreshDelegate(this));
  }

  public readonly signIn = async (
    signInBody: SignInRequest
  ): Promise<SignInResponse> => {
    const signInResponse: SignInResponse =
      await this.httpClient.post<SignInResponse>('', {
        body: {
          AuthFlow: 'CUSTOM_AUTH',
          ClientId: Config.userPoolWebClientId,
          AuthParameters: {
            USERNAME: signInBody.email,
          },
        },
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        isAuthorized: false,
      });
    return signInResponse;
  };

  public readonly signUp = async (signUpBody: SignUpRequest): Promise<{}> => {
    const signUpResponse = await this.httpClient.post<{}>('', {
      body: {
        ClientId: Config.userPoolWebClientId,
        Username: signUpBody.email,
        Password: randomPassword(32),
        UserAttributes: [
          { Name: 'preferred_username', Value: signUpBody.username },
        ],
        ValidationData: [],
      },
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
      },
      isAuthorized: false,
    });
    return signUpResponse;
  };

  public readonly refresh = async (
    refreshToken: string
  ): Promise<TokensResponse> => {
    if (this.waitingPromise) {
      return this.waitingPromise;
    }
    this.waitingPromise = this.refreshCall(refreshToken);
    return this.waitingPromise;
  };

  public readonly refreshCall = async (
    refreshToken: string
  ): Promise<TokensResponse> => {
    try {
      const refreshResponse =
        await this.httpClient.post<GetTokensFromRefreshTokenResponse>('', {
          body: {
            ClientId: Config.userPoolWebClientId,
            RefreshToken: refreshToken,
          },
          headers: {
            'X-Amz-Target':
              'AWSCognitoIdentityProviderService.GetTokensFromRefreshToken',
          },
        });
      // console.log('response: %j', refreshResponse);
      await this.saveTokens(refreshResponse);
      this.waitingPromise = null;
      return refreshResponse;
    } catch (e) {
      await this.revokeToken();
      this.waitingPromise = null;
      this.onAuthenticationFailed();
      throw e;
    }
  };

  public readonly verifyCode = async (
    verifyCodeBody: VerifyCodeRequest
  ): Promise<TokensResponse> => {
    const verifyCodeResponse = await this.httpClient.post<TokensResponse>('', {
      body: {
        ChallengeResponses: {
          USERNAME: verifyCodeBody.email,
          ANSWER: verifyCodeBody.otp,
        },
        ChallengeName: 'CUSTOM_CHALLENGE',
        ClientId: Config.userPoolWebClientId,
        Session: verifyCodeBody.session,
      },
      headers: {
        'X-Amz-Target':
          'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
      },
    });

    await this.saveTokens(verifyCodeResponse);
    return verifyCodeResponse;
  };

  public readonly revokeToken = async () => {
    this.authConsumers.forEach(consumer => {
      consumer.removeAuth();
    });
    await this.storageProvider.removeItem(StorageKeys.ACCESS_TOKEN_KEY);
    await this.storageProvider.removeItem(StorageKeys.REFRESH_TOKEN_KEY);
  };

  private readonly saveTokens = async (refreshResponse: TokensResponse) => {
    this.authConsumers.forEach(consumer => {
      consumer.setupAuth(
        refreshResponse.AuthenticationResult.AccessToken,
        refreshResponse.AuthenticationResult.RefreshToken
      );
    });
    await this.storageProvider.setItem(
      StorageKeys.ACCESS_TOKEN_KEY,
      refreshResponse.AuthenticationResult.AccessToken
    );
    if (refreshResponse.AuthenticationResult.RefreshToken) {
      await this.storageProvider.setItem(
        StorageKeys.REFRESH_TOKEN_KEY,
        refreshResponse.AuthenticationResult.RefreshToken
      );
    }
  };
}
